import React, { useState, useRef } from "react";
import { useToast } from "./ui/use-toast";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Progress } from "./ui/progress";
import { useUser } from "./UserContext";
import axios from "axios";
import { API_BASE_URL } from "../config";
import { FUNCTION_NAMES } from "../constants";
import { extractTimestamp, parseTimestamp } from "../utils/imageUtils"; 
import { Loader2, Users } from "lucide-react";
import { fetchWithRetry } from "../utils/tourUtils";
import UserSelector from "./UserSelector";
import { Label } from "./ui/label";

const UploadImages = ({ projectName }) => {
  const [photos, setPhotos] = useState([]); // Store photos data
  const [uploadProgress, setUploadProgress] = useState({}); // Track progress of each photo
  const [isUploading, setIsUploading] = useState(false); // Track if uploading is in progress
  const [totalProgress, setTotalProgress] = useState(0); // Total progress of all uploads
  const [timeRemaining, setTimeRemaining] = useState(null); // Track the start time for remaining time estimation

  const fileInputRef = useRef(null); // Ref to trigger file input
  const { toast } = useToast(); // Toasts for success/error notifications
  const { selectedUser, selectedUserEmail } = useUser(); // Get the selected username from context
  const [isProcessing, setIsProcessing] = useState(false);
  const [successfulUploads, setSuccessfulUploads] = useState(0);
  const [successfulThumbnails, setSuccessfulThumbnails] = useState(0);
  const [uploadAsUser, setUploadAsUser] = useState(''); // User to upload images as
  const [matchedSyncTours, setMatchedSyncTours] = useState([]); // Matched SyncTour records
  const [autoCreatedTours, setAutoCreatedTours] = useState([]); // Auto-created tours
  // Handle file selection (via file input or drag-and-drop)
  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files).filter((file) =>
      file.type.startsWith("image/"),
    );
    await handleFiles(files);
  };
  const [isDragOver, setIsDragOver] = useState(false);
  const MAX_FILES = 1800;

  // Handle drag events
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    // debugger;
    setIsDragOver(false);
    setIsProcessing(true);

    const files = Array.from(e.dataTransfer.files)
      .filter((file) => file.type.startsWith("image/"))
      .slice(0, MAX_FILES); // ⬅ limit

    if (files.length === MAX_FILES) {
      toast({
        title: "Limit reached",
        description: `Only the first ${MAX_FILES} images were accepted.`,
      });
    }

    await handleFiles(files);
    setIsProcessing(false);
  };
  // Process the selected files
  const handleFiles = async (files) => {
    const newPhotos = [...photos]; // Make a copy of current photos state

    const processFile = (file) => {
      return new Promise((resolve) => {
      const fileName = file.name.replace(/\.[^/.]+$/, "");
      const isDuplicate = newPhotos.some(
        (photo) =>
          photo.name === fileName &&
          photo.sizeMB === (file.size / (1024 * 1024)).toFixed(2),
      );

      if (isDuplicate) {
        console.log(`Duplicate file skipped: ${file.name}`);
          resolve(null); // Skip this file
          return;
      }

      const photoId = Date.now() + Math.random(); // Generate a unique ID for each photo
      setUploadProgress((prev) => ({ ...prev, [photoId]: 0 })); // Initialize upload progress

      const reader = new FileReader();
        reader.onload = () => {
        const { date, time } = extractTimestamp(file.name); // Extract timestamp from filename
        const dateString = `${date.slice(6)}-${date.slice(3, 5)}-${date.slice(0, 2)}T${time}`;

        const photo = {
          id: photoId,
          file,
          dateString,
          sizeMB: (file.size / (1024 * 1024)).toFixed(2),
          name: file.name.replace(/\.[^/.]+$/, ""),
          timestamp: { date, time },
          isUploading: false,
        };
          resolve(photo);
        };
        reader.readAsDataURL(file); // Read file as base64
      });
    };

    // Process all files
    const photoPromises = Array.from(files).map(processFile);
    const processedPhotos = await Promise.all(photoPromises);
    const validPhotos = processedPhotos.filter(photo => photo !== null);
    
    // Update state with all valid photos
    setPhotos([...newPhotos, ...validPhotos]);
  };

  // Process uploaded images through SyncTour system on server
  const processSyncTours = async (uploadedPhotos, userId) => {
    try {
      console.log(`Processing ${uploadedPhotos.length} uploaded photos through SyncTour system...`);
      
      const response = await fetchWithRetry({
        url: `${API_BASE_URL}/api/post`,
        data: {
          function_name: FUNCTION_NAMES.POST.PROCESS_SYNC_TOURS,
          payload: {
            uploadedPhotos: uploadedPhotos.map(photo => ({
              fileName: photo.name + '.jpg',
              s3Path: photo.imageKey
            })),
            userId: userId
          },
        },
        label: 'process sync tours',
      });

      console.log('SyncTour processing response:', response);

      if (response.matchedCount > 0) {
        setMatchedSyncTours(response.matched || []);
        toast({
          title: 'SyncTour Processing',
          description: `Matched ${response.matchedCount} images to SyncTour records`,
        });
      }

      if (response.createdTours && response.createdTours.length > 0) {
        setAutoCreatedTours(response.createdTours);
        toast({
          title: 'Tours Auto-Created!',
          description: `Created ${response.createdTours.length} tour(s) with depth processing started`,
        });
      }

      return response;
    } catch (error) {
      console.error('Error processing SyncTours:', error);
      toast({
        title: 'Warning',
        description: 'Images uploaded but SyncTour processing failed',
        variant: 'destructive',
      });
      return { matchedCount: 0, createdTours: [] };
    }
  };

  const calculateRemainingTime = (
    timeTaken,
    completedUploads,
    totalFiles,
    timeTakenTotal,
  ) => {
    if (completedUploads === 0) return 0; // Avoid division by zero

    const avgTimePerUpload = timeTakenTotal / completedUploads; // Average time per upload
    const remainingUploads = totalFiles - completedUploads;
    const estimatedRemainingTime = avgTimePerUpload * remainingUploads;

    return estimatedRemainingTime;
  };

  const uploadFileWithRetry = async (photo, signedUrlData, maxRetries = 3) => {
    let attempt = 0;
    while (attempt < maxRetries) {
      try {
        await axios.put(signedUrlData.url, photo.file, {
          headers: { "Content-Type": photo.file.type },
          onUploadProgress: (progressEvent) => {
            const percent = (progressEvent.loaded / progressEvent.total) * 100;
            setUploadProgress((prev) => ({ ...prev, [photo.id]: percent }));
          },
        });

        setUploadProgress((prev) => ({ ...prev, [photo.id]: 100 }));
        return {
          success: true,
          name: photo.name,
          imageKey: signedUrlData.key,
          thumbnailKey: signedUrlData.key.replace("/images/", "/thumbnails/"),
        };
      } catch (error) {
        attempt++;
        if (attempt < maxRetries) {
          toast({
            title: `Retrying (${attempt}/${maxRetries})`,
            description: `Retrying upload for ${photo.name}`,
          });
          await new Promise((res) => setTimeout(res, 1500)); // Optional delay between retries
        } else {
          toast({
            title: "Upload failed",
            description: `Could not upload ${photo.name} after ${maxRetries} attempts.`,
            variant: "destructive",
          });
          return { success: false };
        }
      }
    }
  };
  const retryRequest = async (requestFn, maxRetries = 3, delay = 1500) => {
    let attempt = 0;

    while (attempt < maxRetries) {
      try {
        return await requestFn(); // Try the request
      } catch (err) {
        attempt++;
        if (attempt >= maxRetries) throw err; // Give up after maxRetries
        await new Promise((res) => setTimeout(res, delay)); // Wait before retrying
      }
    }
  };
  const handleSave = async () => {
    if (photos.length === 0) {
      toast({
        title: "Error",
        description: "Please upload at least one photo",
        variant: "destructive",
      });
      return;
    }
    setSuccessfulUploads(0);
    setSuccessfulThumbnails(0);
    setIsUploading(true);
    setUploadProgress({});
    setTotalProgress(0);
    const startTime = Date.now();

    try {
      const filesPayload = photos.map((photo) => ({
        fileName: photo.file.name,
        fileType: photo.file.type,
      }));
      const signedUrls = await fetchWithRetry({
        url: `${API_BASE_URL}/api/post`,
        data: {
          function_name: FUNCTION_NAMES.POST.GENERATE_UPLOAD_URL,
          payload: {
            files: filesPayload,
            user: uploadAsUser || selectedUser,
          },
        },
        label: "generate upload urls",
      });

      const allUploadedPhotos = [];
      let currentBatch = [];
      let completedUploads = 0;
      const batchRequests = [];

      for (const photo of photos) {
        setUploadProgress((prev) => ({ ...prev, [photo.id]: 0 }));

        const match = signedUrls.find(
          (item) => item.fileName === photo.file.name,
        );
        if (!match) continue;

        const result = await uploadFileWithRetry(photo, match);

        if (result.success) {
          const uploaded = {
            name: result.name,
            imageKey: result.imageKey,
            thumbnailKey: result.thumbnailKey,
          };

          allUploadedPhotos.push(uploaded); // Track all uploads
          setSuccessfulUploads((prev) => prev + 1); // ✅ add this
          currentBatch.push(uploaded); // Track batch of 4
          completedUploads++;
          // When batch is ready or done uploading all
          const shouldSendBatch =
            currentBatch.length === 4 || completedUploads === photos.length;
          if (shouldSendBatch) {
            const batchToSend = [...currentBatch];
            currentBatch = [];

            const batchPromise = retryRequest(() =>
              fetchWithRetry({
                url: `${API_BASE_URL}/api/post`,
                data: {
                  function_name: FUNCTION_NAMES.POST.CREATE_THUMBNAILS,
                  payload: batchToSend,
                },
                label: "create thumbnails",
              }),
            )
              .then(() => {
                setSuccessfulThumbnails((prev) => prev + batchToSend.length); // ✅ add this
                toast({
                  title: "Success",
                  description: `Processed ${batchToSend.length} thumbnails`,
                });
              })
              .catch((err) => {
                toast({
                  title: "Batch thumbnail processing failed",
                  description: err.message,
                  variant: "destructive",
                });
              });

            batchRequests.push(batchPromise);
          }
          setTotalProgress((completedUploads / photos.length) * 100);

          const timeTakenTotal = (Date.now() - startTime) / 1000;
          const remainingTime = calculateRemainingTime(
            timeTakenTotal,
            completedUploads,
            photos.length,
            timeTakenTotal,
          );
          setTimeRemaining(remainingTime.toFixed(2));
        }
      }
      await Promise.all(batchRequests);

      // Process uploaded images through SyncTour system on server
      if (allUploadedPhotos.length > 0) {
        await processSyncTours(
          allUploadedPhotos, 
          uploadAsUser || selectedUser
        );
      }
    } catch (error) {
      console.error("Error saving project:", error);
      toast({
        title: "Error",
        description: error.message || "Error saving project. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
      setUploadProgress({});
      setPhotos([]);
      // Don't clear matched tours and auto-created tours to show results
    }
  };
  const sortedPhotos = [...photos].sort((a, b) => {
    debugger;

    return parseTimestamp(a.timestamp) - parseTimestamp(b.timestamp);
  });
  console.log(sortedPhotos);

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header with User Selection - Sticky */}
      <div className="sticky top-0 z-10 bg-white border-b border-gray-200 shadow-sm">
        <div className="p-4 space-y-4">
          {/* User Selection */}
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
            <div className="flex items-center gap-3">
              <Users className="h-5 w-5 text-gray-600" />
              <Label className="font-semibold text-gray-800 whitespace-nowrap">Upload as User:</Label>
            </div>
            <div className="flex-1 max-w-xs">
              <UserSelector 
                selectedUser={uploadAsUser} 
                setSelectedUser={setUploadAsUser} 
              />
            </div>
            {uploadAsUser && (
              <div className="text-sm text-blue-600 font-medium px-3 py-1 bg-blue-50 rounded-full">
                ✓ {uploadAsUser}
              </div>
            )}
            {!uploadAsUser && selectedUser && (
              <div className="text-xs text-gray-500 bg-gray-50 px-3 py-1 rounded-full">
                Default: {selectedUser}
              </div>
            )}
          </div>

          {/* SyncTour Status */}
          {matchedSyncTours.length > 0 && (
            <div className="border-t border-gray-200 pt-4">
              <div className="flex items-center gap-3 mb-3">
                <Label className="font-semibold text-gray-800">🔗 SyncTour Matches:</Label>
              </div>
              <div className="text-sm text-green-600 bg-green-50 px-3 py-2 rounded-lg">
                ✓ {matchedSyncTours.length} image(s) matched to existing SyncTour records
              </div>
            </div>
          )}

          {/* Auto-Created Tours */}
          {autoCreatedTours.length > 0 && (
            <div className="border-t border-gray-200 pt-4">
              <div className="flex items-center gap-3 mb-3">
                <Label className="font-semibold text-gray-800">🎯 Auto-Created Tours:</Label>
              </div>
              <div className="space-y-2">
                {autoCreatedTours.map((tour, index) => (
                  <div key={tour.id} className="text-sm text-blue-600 bg-blue-50 px-3 py-2 rounded-lg">
                    ✓ Tour "{tour.name}" created with depth processing started
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
            <div className="text-sm text-gray-600">
              Current User: <span className="font-medium">{selectedUserEmail}</span>
            </div>
            <div className="flex gap-2">
              <Button 
                onClick={handleSave} 
                disabled={isUploading || !selectedUser || photos.length === 0}
                className="flex-1 sm:flex-none"
              >
        {isUploading ? (
          <div className="flex items-center gap-2">
            <Loader2 className="animate-spin w-4 h-4" />
            Uploading...
          </div>
        ) : (
                  `Save Photos (${photos.length})`
        )}
      </Button>
              
              {(matchedSyncTours.length > 0 || autoCreatedTours.length > 0) && (
                <Button 
                  variant="outline"
                  onClick={() => {
                    setMatchedSyncTours([]);
                    setAutoCreatedTours([]);
                    setSuccessfulUploads(0);
                    setSuccessfulThumbnails(0);
                  }}
                  className="flex-shrink-0"
                >
                  Clear Results
                </Button>
              )}
            </div>
          </div>
          
          {isProcessing && (
            <div className="text-sm text-amber-600 bg-amber-50 px-3 py-1 rounded-full">
              Processing dropped files...
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="p-4 space-y-6">
          
          {/* Drag and Drop Area */}
      {!isUploading && (
        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
              className={`w-full h-[50vh] border-4 border-dashed rounded-lg p-8 flex items-center justify-center transition-colors duration-200 cursor-pointer ${
            isDragOver
              ? "border-blue-500 bg-blue-100"
              : "border-gray-300 bg-gray-100"
          }`}
          onClick={() => fileInputRef.current.click()}
        >
              <div className="text-center">
                <p className="text-gray-600 text-lg mb-2">
            Drag & Drop Images Here or Click to Select
          </p>
                <p className="text-gray-400 text-sm">
                  Will upload as: <span className="font-medium">{uploadAsUser || selectedUser}</span>
                </p>
                <p className="text-gray-400 text-xs mt-1">
                  Images will be matched to existing SyncTour records by filename
                </p>
              </div>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            multiple
            accept="image/*"
            className="hidden"
          />
        </div>
      )}

          {/* Upload Progress */}
      {isUploading && (
            <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-gray-800">Uploading Photos</h3>
                  <span className="text-sm text-gray-600">{Math.round(totalProgress)}% Complete</span>
                </div>
          <Progress value={totalProgress} />
                {timeRemaining && (
                  <p className="text-sm text-gray-600">
                    Time remaining: {timeRemaining} seconds
                  </p>
                )}
              </div>
        </div>
      )}

          {/* Success Message */}
      {!isUploading && successfulUploads > 0 && (
            <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
              <div className="text-green-700 font-medium space-y-1">
                <div>✅ Uploaded {successfulUploads} photo{successfulUploads !== 1 ? "s" : ""}</div>
                <div>🖼️ Successfully processed {successfulThumbnails} thumbnails</div>
                {matchedSyncTours.length > 0 && (
                  <div>🔗 Matched {matchedSyncTours.length} SyncTour record{matchedSyncTours.length !== 1 ? "s" : ""}</div>
                )}
                {autoCreatedTours.length > 0 && (
                  <div>🎯 Auto-created {autoCreatedTours.length} tour{autoCreatedTours.length !== 1 ? "s" : ""} with depth processing</div>
                )}
              </div>
        </div>
      )}

          {/* Photos List */}
      {photos.length > 0 && (
        <div className="space-y-4">
              <h3 className="font-semibold text-gray-800">Selected Photos ({photos.length})</h3>
              <div className="space-y-3">
          {sortedPhotos.map((photo, i) => (
                  <Card key={photo.id} className="hover:shadow-sm transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex justify-between items-start">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-sm font-medium">
                            {i + 1}
                          </div>
                          <div>
                            <div className="font-medium text-gray-800">
                              {photo.name}
                            </div>
                  <div className="text-sm text-gray-500">
                              {photo.sizeMB} MB • {photo.timestamp.date} - {photo.timestamp.time}
                            </div>
                          </div>
                  </div>
                </div>
                      {uploadProgress[photo.id] !== undefined && (
                        <div className="mt-3 space-y-1">
                <Progress value={uploadProgress[photo.id] || 0} />
                          <p className="text-xs text-gray-500">
                            {Math.round(uploadProgress[photo.id] || 0)}%
                          </p>
                        </div>
                      )}
              </CardContent>
            </Card>
          ))}
              </div>
        </div>
      )}
        </div>
      </div>
    </div>
  );
};

export default UploadImages;
